import { ArrowForward, BuildCircle } from "@mui/icons-material";
import { Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { format, parse } from "date-fns";
import { useTranslation } from "react-i18next";

import { GqlInvoicePeriod } from "~/__generated__";
import GaiaPageGridComponent from "~/modules/app/components/GaiaPageGridComponent";
import { parseStringToDate } from "~/modules/app/util/dateHelpers";
import { formatCents } from "~/modules/app/util/formatters";

interface InvoiceAvailablePeriodsListProps {
    data: GqlInvoicePeriod[];
    onClick: (invoiceId: string) => void;
    onCreateClick: (year: number, month: number) => void;
}

export function InvoiceAvailablePeriodsList({ data, onClick, onCreateClick }: InvoiceAvailablePeriodsListProps) {
    const { t } = useTranslation("invoice");

    return (
        <GaiaPageGridComponent title={t("list.title")}>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t("list.table.year")}</TableCell>
                            <TableCell>{t("list.table.month")}</TableCell>
                            <TableCell>{t("list.table.createdAt")}</TableCell>
                            <TableCell>{t("list.table.amount")}</TableCell>
                            <TableCell>{t("list.table.numberOfRecords")}</TableCell>
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.map(element => {
                            const hasInvoiceAvailable = element.invoiceId !== null && element.invoiceId !== undefined;

                            const handleOnClick = () => {
                                if (!hasInvoiceAvailable) return;
                                onClick(element.invoiceId!);
                            };

                            const handleCreateClick = () => {
                                onCreateClick(element.year, element.month);
                            };

                            return (
                                <TableRow
                                    key={`${element.year}-${element.month}-${element.invoiceId ?? "invoice-unavailable"}`}
                                    onClick={handleOnClick}
                                    sx={{
                                        cursor: hasInvoiceAvailable ? "pointer" : "default",
                                    }}
                                >
                                    <TableCell>{element.year}</TableCell>
                                    <TableCell>
                                        {format(
                                            parse(`${element.year}-${element.month}`, "yyyy-L", new Date()),
                                            "MMMM",
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {element.createdAt
                                            ? format(parseStringToDate(element.createdAt), "do MMMM")
                                            : null}
                                    </TableCell>
                                    <TableCell>
                                        {element.totalCostInCents !== null
                                            ? formatCents(element.totalCostInCents ?? 0)
                                            : null}
                                    </TableCell>
                                    <TableCell>{element.numberOfRecords}</TableCell>
                                    <TableCell>
                                        {hasInvoiceAvailable ? (
                                            <Button startIcon={<ArrowForward />} size="small">
                                                {t("list.table.actions.view")}
                                            </Button>
                                        ) : (
                                            <Button
                                                onClick={handleCreateClick}
                                                startIcon={<BuildCircle />}
                                                size="small"
                                            >
                                                {t("list.table.actions.create")}
                                            </Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </GaiaPageGridComponent>
    );
}
